import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MuiAvatar from '@mui/material/Avatar';
import MuiListItemAvatar from '@mui/material/ListItemAvatar';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import Select, { selectClasses } from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import GrassRoundedIcon from '@mui/icons-material/GrassRounded';
import { useGrow, GROW_STAGES } from '../context/GrowContext';

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  width: 28,
  height: 28,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.secondary,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
}));

const ListItemAvatar = styled(MuiListItemAvatar)({
  minWidth: 0,
  marginRight: 12,
});

export default function SelectContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { grows, activeGrowId, setActiveGrowId } = useGrow();

  // Determine current selection based on route
  const getCurrentValue = React.useCallback(() => {
    if (location.pathname === '/') {
      return 'dashboard';
    }
    if (location.pathname.startsWith('/grow/')) {
      const growId = location.pathname.split('/grow/')[1];
      if (grows.find(g => g.id === growId)) {
        return growId;
      }
    }
    return 'dashboard';
  }, [location.pathname, grows]);

  const [selectedValue, setSelectedValue] = React.useState(getCurrentValue);

  // Update selection when route changes
  React.useEffect(() => {
    setSelectedValue(getCurrentValue());
  }, [getCurrentValue]);

  const handleChange = (event) => {
    const value = event.target.value;

    if (value === 'dashboard') {
      setActiveGrowId(null);
      navigate('/');
    } else if (value === 'new-grow') {
      // Navigate to dashboard and trigger new grow dialog
      navigate('/?new=true');
    } else {
      // It's a grow ID
      setActiveGrowId(value);
      navigate(`/grow/${value}`);
    }
    setSelectedValue(value);
  };

  return (
    <Select
      labelId="grow-select"
      id="grow-simple-select"
      value={selectedValue}
      onChange={handleChange}
      displayEmpty
      inputProps={{ 'aria-label': 'Select view' }}
      fullWidth
      sx={{
        maxHeight: 56,
        width: 215,
        '&.MuiList-root': {
          p: '8px',
        },
        [`& .${selectClasses.select}`]: {
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          pl: 1,
        },
      }}
    >
      <ListSubheader sx={{ pt: 0 }}>Overview</ListSubheader>
      <MenuItem value="dashboard">
        <ListItemAvatar>
          <Avatar alt="Dashboard">
            <Box component="img" src="/icon.svg" alt="" sx={{ width: 24, height: 24 }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary="Dashboard" secondary="All grows overview" />
      </MenuItem>

      {grows.filter(g => g.status === 'active').length > 0 && (
        <ListSubheader>Active Grows</ListSubheader>
      )}
      {grows.filter(g => g.status === 'active').map((grow) => (
        <MenuItem key={grow.id} value={grow.id}>
          <ListItemAvatar>
            <Avatar
              alt={grow.name}
              sx={{
                bgcolor: grow.id === activeGrowId ? 'primary.main' : undefined,
                color: grow.id === activeGrowId ? 'white' : undefined,
              }}
            >
              <GrassRoundedIcon sx={{ fontSize: '1rem' }} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={grow.name}
            secondary={GROW_STAGES[grow.currentStage]?.label || 'Unknown stage'}
          />
        </MenuItem>
      ))}

      <Divider sx={{ mx: -1 }} />
      <MenuItem value="new-grow">
        <ListItemIcon>
          <AddRoundedIcon />
        </ListItemIcon>
        <ListItemText primary="Start New Grow" />
      </MenuItem>
    </Select>
  );
}
